/**
 * studio-shop D1 — the shop plug-in: a studio sells goods on its website, made
 * and shipped by the fulfilment services it connects, paid on its own Stripe
 * account. A paid add-on, switched on per studio by the operator (`shop`).
 * Everything the shop adds is listed here and grows as it is built; the check
 * (`pnpm plugins:check`, the API's boot) fails on anything built but undeclared.
 */
import { definePlugin } from './define.js';

export const shopPlugin = definePlugin({
  id: 'shop',
  name: 'Shop',
  description:
    "Sell goods on the studio's website: a product grid and window, a bag, checkout as a guest or signed in with Telegram, payment on the studio's own Stripe account, order emails (or Telegram messages for a buyer who links their order), and the studio's new-order and problem notices in Telegram. Products are made and shipped by the fulfilment services the studio connects.",
  paidAddOn: true,
  feature: 'shop',
  pieces: ['shop-grid', 'shop-product', 'shop-bag-button', 'shop-bag', 'shop-checkout', 'shop-payment-step', 'shop-order'],
  pages: ['shop-bag', 'shop-checkout', 'shop-order'],
  // D1, off stops selling and keeps servicing: a studio with orders still reaches the routes marked `servicing`.
  siteRoutes: {
    prefix: '/api/site/shop',
    routes: [
      { method: 'GET', url: '/api/site/shop/products' },
      { method: 'POST', url: '/api/site/shop/bag/price' },
      { method: 'GET', url: '/api/site/shop/checkout/details' },
      { method: 'POST', url: '/api/site/shop/quote' },
      { method: 'POST', url: '/api/site/shop/orders' },
      { method: 'POST', url: '/api/site/shop/orders/:id/intent' },
      { method: 'GET', url: '/api/site/shop/orders/:id', servicing: true },
      { method: 'POST', url: '/api/site/shop/orders/:id/link' },
    ],
  },
  adminRoutes: {
    prefix: '/api/admin/shop',
    routes: [
      { method: 'POST', url: '/api/admin/shop/services/:service/connect', servicing: true },
      { method: 'GET', url: '/api/admin/shop/services/:service/callback', servicing: true },
      { method: 'GET', url: '/api/admin/shop/services/:service/pending', servicing: true },
      { method: 'POST', url: '/api/admin/shop/services/:service/pending/confirm', servicing: true },
      { method: 'DELETE', url: '/api/admin/shop/services/:service/pending', servicing: true },
      { method: 'GET', url: '/api/admin/shop/services', servicing: true },
      // shop-self-fulfilment D1, D4: Shipped by you turned on, and where it ships.
      { method: 'PUT', url: '/api/admin/shop/services/self' },
      { method: 'GET', url: '/api/admin/shop/connections/:id', servicing: true },
      { method: 'POST', url: '/api/admin/shop/connections/:id/sync' },
      { method: 'GET', url: '/api/admin/shop/settings' },
      { method: 'PUT', url: '/api/admin/shop/settings' },
      { method: 'GET', url: '/api/admin/shop/products' },
      // shop-self-fulfilment D3: a product the studio adds by hand, and how many are left.
      { method: 'POST', url: '/api/admin/shop/products' },
      { method: 'PUT', url: '/api/admin/shop/products/order' },
      { method: 'GET', url: '/api/admin/shop/products/:id' },
      { method: 'PATCH', url: '/api/admin/shop/products/:id' },
      { method: 'PUT', url: '/api/admin/shop/products/:id/stock' },
      { method: 'GET', url: '/api/admin/shop/overview' },
      { method: 'GET', url: '/api/admin/shop/orders', servicing: true },
      { method: 'GET', url: '/api/admin/shop/orders/:orderId', servicing: true },
      { method: 'POST', url: '/api/admin/shop/orders/:orderId/parts/:partId/retry', servicing: true },
      // shop-self-fulfilment D6: her part marked shipped, so an order can be finished while the shop is off.
      { method: 'POST', url: '/api/admin/shop/orders/:orderId/parts/:partId/shipped', servicing: true },
      { method: 'POST', url: '/api/admin/shop/orders/:orderId/cancel', servicing: true },
      { method: 'POST', url: '/api/admin/shop/orders/:orderId/refunds/:refundId/again', servicing: true },
      { method: 'POST', url: '/api/admin/shop/orders/:orderId/refund', servicing: true },
    ],
  },
  adminSections: ['shop'],
  noticeTypes: ['shop.orderPaid', 'shop.orderProblem'],
  clientMessageTypes: ['shop_order_shipped', 'shop_order_refunded', 'shop_order_refund_failed', 'shop_order_problem', 'shop_order_confirmed'],
  emailKinds: ['shop_receipt', 'shop_shipped', 'shop_refunded', 'shop_refund_failed', 'shop_problem'],
  services: [
    {
      id: 'printful',
      name: 'Printful',
      description:
        "Prints and ships the studio's products on demand. The studio approves Telify's Printful app for a store made for custom sites (Connect via API); Printful charges the studio's card for each order when it is confirmed.",
      connect: 'oauth',
      paidAddOn: false,
    },
    {
      // shop-self-fulfilment D1: the studio's own service, calling nothing outside Telify.
      id: 'self',
      name: 'Shipped by you',
      description:
        'Things the studio makes or keeps and ships itself. The studio adds each product by hand, with its own picture, price and how many are left, sets where it ships and for how much, and marks each order shipped.',
      connect: 'none',
      paidAddOn: false,
    },
  ],
  siteOptions: {
    // D8, The Telegram offer (Gabriel 2026-10-26): general words for every site; a custom site sets its own.
    signInOffer: {
      kind: 'group',
      description: 'The Telegram sign-in offer on the thank-you page: its heading, the line under it, and what signing in gives.',
      fields: {
        heading: { kind: 'text', maxLength: 60, description: 'Its heading.' },
        lead: { kind: 'text', maxLength: 160, description: 'The line under the heading.' },
        benefits: {
          kind: 'list',
          min: 1,
          max: 6,
          description: 'What signing in gives, one to six, in order.',
          item: {
            kind: 'group',
            description: 'One thing signing in gives.',
            fields: {
              title: { kind: 'text', maxLength: 40, description: 'Its title.' },
              line: { kind: 'text', maxLength: 90, description: 'The line under its title.' },
              icon: { kind: 'choice', values: ['truck', 'spark', 'calendar', 'chat', 'fast', 'list'], optional: true, description: 'Its icon; none when left out.' },
            },
          },
        },
      },
      default: {
        heading: 'Follow your order in Telegram',
        lead: 'Sign in with Telegram and this order comes with you. No password, nothing to fill in.',
        benefits: [
          { title: 'Follow your order', line: 'Updates as it’s made and shipped, right in Telegram.', icon: 'truck' },
          { title: 'Hear what’s new', line: 'New work and offers from the studio.', icon: 'spark' },
          { title: 'Faster checkout next time', line: 'Your address is filled in for you.', icon: 'fast' },
        ],
      },
    },
  },
});
